import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Checkbox, DialogActions, DialogContent, DialogTitle, ListItem, ListItemText } from '@mui/material';
import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ControlpanelPrimaryButton, ControlpanelSecondaryButton, GrayIconButton } from '../../../components/StyledButton';
import { CustomDialog, CustomList } from '../../../components/StyledComponents';
import { StyledLinkBlue } from '../../../components/StyledLink';
import CustomDatePicker from './../../../components/CustomDatePicker';
import ExportDataDialog from './ExportDataDialog';

const ExportSelectUserDataDialog = ({ info, onClose }) => {
   const { t } = useTranslation();
   const [checkedItems, setCheckedItems] = useState({
      userProfileInfo: false,
      userLocationRestrictions: false,
      userGroupInfo: false,
      userGroupMemberships: false,
      groupAdmins: false,
      userGroupLocationRestrictions: false,
      shifts: false,
      rosters: false,
      rosterItems: false,
      leaves: false,
      holidays: false,
      eventLogs: false,
   });

   const handleSelectAll = () => {
      const updatedCheckedItems = {};
      const areAllItemsChecked = Object.values(checkedItems).every((value) => value);
      for (const key in checkedItems) {
         updatedCheckedItems[key] = areAllItemsChecked ? false : true;
      }
      setCheckedItems(updatedCheckedItems);
   };

   const [openExportSelectedEmployee, setOpenExportSelectedEmployee] = useState(false);

   const handleOpenExportSelectedEmployee = () => {
      setOpenExportSelectedEmployee(true);
   };

   const handleCloseExportSelectedEmployee = () => {
      setOpenExportSelectedEmployee(false);
   };

   return (
      <Fragment>
         <CustomDialog transitionDuration={300} maxWidth={'xl'} open onClose={onClose} keepMounted>
            <DialogTitle>
               <div className='flex justify-between'>
                  <div className='flex items-center gap-4'>
                     <p className='text-xl font-semibold '>
                        Select data to export - {info.firstName}&nbsp;{info.lastName}
                     </p>
                  </div>
                  <GrayIconButton onClick={onClose}>
                     <CloseIcon />
                  </GrayIconButton>
               </div>
            </DialogTitle>

            <DialogContent>
               <div className='pt-5'>
                  <p>
                     Select the data you want to export. Selected data can be downloaded directly to your PC, or you can specify a FTP
                     location to save it.
                  </p>
                  <div className='lg:flex lg:justify-between lg:flex-row-reverse flex-wrap'>
                     <div className='pt-5'>
                        <StyledLinkBlue onClick={handleSelectAll} className='cursor-pointer'>
                           Select all
                        </StyledLinkBlue>
                     </div>
                     <div className='relative'>
                        <CustomList>
                           <ListItem>
                              <Checkbox
                                 id='userProfileInfo'
                                 checked={checkedItems.userProfileInfo}
                                 onChange={() => setCheckedItems({ ...checkedItems, userProfileInfo: !checkedItems.userProfileInfo })}
                              />
                              <ListItemText primary='User profile information' />
                           </ListItem>
                           <ListItem className='!pl-10'>
                              <Checkbox
                                 id='userLocationRestrictions'
                                 checked={checkedItems.userLocationRestrictions}
                                 onChange={() =>
                                    setCheckedItems({ ...checkedItems, userLocationRestrictions: !checkedItems.userLocationRestrictions })
                                 }
                              />
                              <ListItemText primary='User location restrictions' />
                           </ListItem>

                           <ListItem>
                              <Checkbox
                                 id='userGroupInfo'
                                 checked={checkedItems.userGroupInfo}
                                 onChange={() => setCheckedItems({ ...checkedItems, userGroupInfo: !checkedItems.userGroupInfo })}
                              />
                              <ListItemText primary='User group information' />
                           </ListItem>
                           <ListItem className='!pl-10'>
                              <Checkbox
                                 id='userGroupMemberships'
                                 checked={checkedItems.userGroupMemberships}
                                 onChange={() =>
                                    setCheckedItems({ ...checkedItems, userGroupMemberships: !checkedItems.userGroupMemberships })
                                 }
                              />
                              <ListItemText primary='User group memberships' />
                           </ListItem>
                           <ListItem className='!pl-10'>
                              <Checkbox
                                 id='groupAdmins'
                                 checked={checkedItems.groupAdmins}
                                 onChange={() => setCheckedItems({ ...checkedItems, groupAdmins: !checkedItems.groupAdmins })}
                              />
                              <ListItemText primary='Group admins' />
                           </ListItem>
                           <ListItem className='!pl-10'>
                              <Checkbox
                                 id='userGroupLocationRestrictions'
                                 checked={checkedItems.userGroupLocationRestrictions}
                                 onChange={() =>
                                    setCheckedItems({
                                       ...checkedItems,
                                       userGroupLocationRestrictions: !checkedItems.userGroupLocationRestrictions,
                                    })
                                 }
                              />
                              <ListItemText primary='User group location restrictions' />
                           </ListItem>

                           <ListItem>
                              <Checkbox
                                 id='shifts'
                                 checked={checkedItems.shifts}
                                 onChange={() => setCheckedItems({ ...checkedItems, shifts: !checkedItems.shifts })}
                              />
                              <ListItemText primary='Shifts' />
                           </ListItem>
                           <ListItem>
                              <Checkbox
                                 id='rosters'
                                 checked={checkedItems.rosters}
                                 onChange={() => setCheckedItems({ ...checkedItems, rosters: !checkedItems.rosters })}
                              />
                              <ListItemText primary='Rosters' />
                           </ListItem>
                           <ListItem className='!pl-10'>
                              <Checkbox
                                 id='rosterItems'
                                 checked={checkedItems.rosterItems}
                                 onChange={() => setCheckedItems({ ...checkedItems, rosterItems: !checkedItems.rosterItems })}
                              />
                              <ListItemText primary='Roster items' />
                           </ListItem>

                           <ListItem>
                              <Checkbox
                                 id='leaves'
                                 checked={checkedItems.leaves}
                                 onChange={() => setCheckedItems({ ...checkedItems, leaves: !checkedItems.leaves })}
                              />
                              <ListItemText primary='Leaves' />
                           </ListItem>
                           <ListItem>
                              <Checkbox
                                 id='holidays'
                                 checked={checkedItems.holidays}
                                 onChange={() => setCheckedItems({ ...checkedItems, holidays: !checkedItems.holidays })}
                              />
                              <ListItemText primary='Holidays' />
                           </ListItem>
                           <ListItem>
                              <Checkbox
                                 id='eventLogs'
                                 checked={checkedItems.eventLogs}
                                 onChange={() => setCheckedItems({ ...checkedItems, eventLogs: !checkedItems.eventLogs })}
                              />
                              <ListItemText primary='Event logs' />
                           </ListItem>

                           <ListItem>
                              <div className='flex flex-col pl-[41px]'>
                                 <p>Export events from this date</p>
                                 <div className='pt-2'>
                                    <CustomDatePicker disabled={!checkedItems.eventLogs} />
                                 </div>
                              </div>
                           </ListItem>
                        </CustomList>
                     </div>
                  </div>
               </div>
            </DialogContent>

            <DialogActions>
               <div className='flex gap-2 px-4 py-4'>
                  <ControlpanelSecondaryButton onClick={onClose}>
                     <CloseIcon />
                     <p>{t('button.cancel')}</p>
                  </ControlpanelSecondaryButton>
                  <ControlpanelPrimaryButton onClick={handleOpenExportSelectedEmployee}>
                     <PlayArrowIcon />
                     <p>{t('button.start-export')}</p>
                  </ControlpanelPrimaryButton>
               </div>
            </DialogActions>
         </CustomDialog>

         {openExportSelectedEmployee && (
            <ExportDataDialog passwordId='exportSelectUserDataFilePassword' onClose={handleCloseExportSelectedEmployee} />
         )}
      </Fragment>
   );
};

export default ExportSelectUserDataDialog;
